import type { Connection } from '@solana/web3.js'
import type { PublicClient } from 'viem'

export const PLATFORMS = [
	'polymarket',
	'kalshi',
	'predictfun',
	'limitless',
	'myriad',
	'hyperliquid',
] as const

export type BridgePlatform = (typeof PLATFORMS)[number]

export type TransferDirection =
	| 'toPolymarket'
	| 'toKalshi'
	| 'toPredictfun'
	| 'toLimitless'
	| 'toMyriad'
	| 'toHyperliquid'

export interface PlatformAddresses {
	eoaAddress?: `0x${string}` | string | null
	solanaAddress?: string | null
}

export interface WalletRuntime extends PlatformAddresses {
	addressOverrides?: Partial<Record<BridgePlatform, string | undefined>>
	publicClient?: PublicClient | null
	basePublicClient?: PublicClient | null
	solanaConnection?: Connection | null
	polymarketSafe?: string | null
	// Active Polymarket funder preference for legacy Safe users
	// ('safe' | 'deposit' | null). 'deposit' routes address resolution to the
	// deposit wallet even when polymarketSafe is set (wallet switcher).
	polymarketActiveWallet?: 'safe' | 'deposit' | null
	// Chance user UUID for the address being resolved. When the address belongs
	// to a user on the LEGACY_BROKEN_SAFES allow-list the polymarket provider
	// returns the deposit wallet regardless of polymarketSafe.
	userId?: string | null
}

export interface PlatformChainConfig {
	chainId: number
	currency: string
}

export interface TokenBalance {
	symbol: string
	raw: bigint
	decimals: number
	amount: number
	formatted: string
	tokenAddress?: string
}

export interface MarketBalance {
	platform: BridgePlatform
	address: string
	/**
	 * Generic spendable/accounting balance for shared wallet surfaces.
	 * This must match the provider's cashToken metadata.
	 */
	cash: TokenBalance
	/**
	 * Market-specific auxiliary balances. These are not safe to use for generic
	 * bridge/accounting decisions unless the consumer explicitly asks for them.
	 */
	secondary?: Record<string, TokenBalance>
}

export interface TokenBalanceRequest {
	address?: string
	tokenAddress: string
	decimals: number
	symbol?: string
}

export interface MarketWalletProvider {
	id: BridgePlatform
	label: string
	direction: TransferDirection
	/**
	 * Decimals for the provider's generic cash token.
	 */
	decimals: number
	/**
	 * Current shared bridge/accounting chain config. This is not a complete
	 * bridge abstraction; Relay flow cleanup happens in a later pass.
	 */
	chain: PlatformChainConfig
	cashToken: {
		symbol: string
		address?: string
		decimals: number
	}
	resolveAddress(runtime: WalletRuntime): string | undefined
	balanceQueryKey(address: string | undefined): readonly unknown[]
	readBalance(runtime: WalletRuntime): Promise<MarketBalance | null>
	readTokenBalance?(
		runtime: WalletRuntime,
		request: TokenBalanceRequest
	): Promise<TokenBalance | null>
}
